import { isString } from '../isString/isString';
/**
 * Convert color string in RGB format to array of numbers of RGB values.
 *
 * @param {string} value Color string in RGB format.
 * @returns {number[]}
 *
 * @example
 * // Converts "rgb(255, 255, 255)" to [255, 255, 255]
 * rgbStringToArray('rgb(255, 255, 255)');
 */
export function rgbStringToArray(value) {
    if (typeof value !== 'string') {
        return [0, 0, 0];
    }
    return value.match(/[-]{0,1}[\d]*[.]{0,1}[\d]+/g)?.map(Number) ?? [0, 0, 0];
}
/**
 * Convert array of numbers in RGB format to array of relative numbers.
 *
 * @param {number[]} value Array of colors in RGB format.
 * @returns {number[]}
 *
 * @example
 * // As example converts "[255, 255, 255]" to [1, 1, 1], because in RGB 255 is 100%
 * rgbArrayToNumeric([255, 255, 255]);
 */
export function rgbArrayToNumeric(value) {
    if (!Array.isArray(value)) {
        return [0, 0, 0];
    }
    if (value.every((i) => isNaN(+i))) {
        return [0, 0, 0];
    }
    return value.map((value, index, array) => {
        // If color value is rgba (e.g. [255, 255, 255, 1]), last value in array should not be modified
        if (array.length === 4 && index === array.length - 1) {
            return value;
        }
        return value / 255;
    });
}
/**
 * Convert color string of RGB value to array of relative numbers.
 *
 * @param {string} value Color string in RGB format.
 * @returns {number[]}
 *
 * @example
 * // As example converts "rgb(255, 255, 255)" to [1, 1, 1], because in RGB 255 is 100%
 * rgbStringToNumeric('rgb(255, 255, 255)');
 */
export function rgbStringToNumeric(value) {
    return rgbArrayToNumeric(rgbStringToArray(value));
}
/**
 * Convert array of relative numbers to string in RGB format.
 *
 * @param {number[]} value Array of relative numbers.
 * @returns {string}
 *
 * @example
 * // As example converts "[1, 1, 1]" to "rgb(255, 255, 255)"
 * rgbArrayToRGBString([1, 1, 1]);
 */
export function rgbArrayToRGBString(value) {
    if (!Array.isArray(value)) {
        return `rgb(0, 0, 0)`;
    }
    if (value.every((i) => isNaN(+i))) {
        return `rgb(0, 0, 0)`;
    }
    const [r, g, b] = value;
    const hexR = Math.round((255 * (r * 100)) / 100);
    const hexG = Math.round((255 * (g * 100)) / 100);
    const hexB = Math.round((255 * (b * 100)) / 100);
    return `rgb(${hexR}, ${hexG}, ${hexB})`;
}
/**
 * Represent string value as HEX color.
 *
 * @param {string} string String value.
 * @returns {string}
 *
 * @example
 * // As example converts "metricsdemo" to "#26a583"
 * stringToColor('metricsdemo');
 */
export function stringToColor(string) {
    if (!string || !isString(string)) {
        return '#000';
    }
    let hash = 0;
    let i;
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
}
